import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSelector } from '../components/LanguageSelector';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moto-dashboard-4/artifacts/tifo19c3_grok_image_1769707994767.jpg";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('auth.fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container relative">
      {/* Language Selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="auth-card chamfer">
        <div className="auth-logo">
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-24 h-24 rounded-lg overflow-hidden">
              <img 
                src={LOGO_URL} 
                alt="Vault Ride" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <h1 className="auth-title">Vault Ride</h1>
          <p className="text-[#9CA3AF] text-xs uppercase tracking-widest mt-1">
            {t('auth.garageManager')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="form-group">
            <Label className="form-label">{t('auth.email')}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="form-input bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-12 font-mono"
              data-testid="login-email"
            />
          </div>

          <div className="form-group">
            <Label className="form-label">{t('auth.password')}</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="form-input bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-12 font-mono"
              data-testid="login-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm"
            data-testid="login-submit"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              t('auth.login')
            )}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-[#9CA3AF] text-sm">
            {t('auth.noAccount')}{' '}
            <Link
              to="/register"
              className="text-[#FF5F1F] hover:underline uppercase tracking-wider text-xs"
              data-testid="register-link"
            >
              {t('auth.register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
